// Reuses rune-lab's actual concept/operator/modifier definitions directly (not a
// duplicated copy) so the game's palette can never drift out of sync with the design
// tool's. Vite's dev-server fs.allow is extended in vite.config.ts to permit reading
// across the sibling rune-lab/ directory, same reasoning as rune-lab's own allowance
// for reading ../design/*.md. If the rune engine's logic (not just its vocabulary)
// ends up needing to live in the game too, extracting a proper shared package is the
// natural next step -- this direct import is the proportionate choice for a palette
// display that doesn't need the resolution engine yet.
import { CONCEPT_LIST } from '../../rune-lab/src/runes/concepts';
import { OPERATOR_LIST } from '../../rune-lab/src/runes/operators';
import { MODIFIERS } from '../../rune-lab/src/runes/modifiers';
import { CATEGORY_COLOR, CONCEPT_SHORTHAND, OPERATOR_SHORTHAND, MODIFIER_SHORTHAND } from '../../rune-lab/src/runes/shorthand';
import { RUNE_SYMBOL } from '../../rune-lab/src/runes/symbols';
import { getDisplayMode, getDevMode, getShowRuneDescriptions } from './settings';
import { hasLearnedRune } from './learnedRunes';

interface RuneLike {
  id: string;
  name: string;
  description: string;
}

// A locked chip shows neither name nor symbol -- even the fact of *which* rune sits
// there is part of what's still undiscovered -- and is a disabled <button>, so it
// never fires a click at all rather than needing every call site to re-check.
const LOCKED_CHIP_HTML = '<button class="rune-board-chip rune-board-chip-locked" disabled>?</button>';

function chipHtml(rune: RuneLike, shorthand: string, color: string): string {
  if (!getDevMode() && !hasLearnedRune(rune.id)) return LOCKED_CHIP_HTML;

  // Developer Tools' "Show rune descriptions" deliberately bypasses the mystery
  // entirely -- it always shows the real shorthand and the real description (the
  // answer key, normally never surfaced anywhere in the game) regardless of
  // display mode. A debugging aid, never a player-facing state.
  if (getShowRuneDescriptions()) {
    return `<button class="rune-board-chip" data-rune-id="${rune.id}" style="color:${color}; border-color:${color}" title="${rune.description}">${shorthand} <span class="rune-board-name">${rune.name}</span></button>`;
  }

  const symbolMode = getDisplayMode() === 'symbols';
  const label = symbolMode ? (RUNE_SYMBOL[rune.id] ?? '?') : shorthand;
  // Symbols mode omits the name and the description tooltip entirely -- showing
  // either would just leak the meaning the mode exists to hide. Note: `description`
  // (rune-lab's real, dev-authored explanation of what a rune does) is intentionally
  // never surfaced here at all, in either mode -- that's the answer key, and showing
  // it anywhere in the game would defeat the discovery-over-explanation pillar.
  const title = symbolMode ? '' : ` title="${rune.name}"`;
  const nameSpan = symbolMode ? '' : ` <span class="rune-board-name">${rune.name}</span>`;
  return `<button class="rune-board-chip" data-rune-id="${rune.id}" style="color:${color}; border-color:${color}"${title}>${label}${nameSpan}</button>`;
}

function sectionHtml(label: string, color: string, chips: string): string {
  return `
    <div class="rune-board-section">
      <span class="rune-board-section-label" style="color:${color}">${label}</span>
      <div class="rune-board-chips">${chips}</div>
    </div>`;
}

export function renderRuneBoard(): string {
  const concepts = CONCEPT_LIST.map((c) => chipHtml(c, CONCEPT_SHORTHAND[c.id], CATEGORY_COLOR.concept)).join('');
  const operators = OPERATOR_LIST.map((o) => chipHtml(o, OPERATOR_SHORTHAND[o.id], CATEGORY_COLOR.operator)).join('');
  const modifiers = MODIFIERS.map((m) => chipHtml(m, MODIFIER_SHORTHAND[m.id] ?? m.id, CATEGORY_COLOR.modifier)).join('');

  return [
    sectionHtml('Concepts', CATEGORY_COLOR.concept, concepts),
    sectionHtml('Operators', CATEGORY_COLOR.operator, operators),
    sectionHtml('Modifiers', CATEGORY_COLOR.modifier, modifiers),
  ].join('');
}
